import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

import { breakpoints } from '../../constants';
import { Flex } from '../grid';
import { Icon } from '../icons';
import { LoadingIndicator } from '../icons/loading-indicator';
import { P } from '../typography';
import { RemoveButton } from '../remove-button';

const StoredCard = ({ id, lastFour, brand, onClick, selected }) => {
  const { PaymentStore } = useRootStore();
  const [isRemovingCard, setIsRemovingCard] = useState(false);

  const removeCard = async () => {
    setIsRemovingCard(true);
    let result;
    try {
      result = await PaymentStore.removeRecurringPaymentMethod({ recurringDetailReference: id });
    } finally {
      setIsRemovingCard(false);

      if (get(result, 'success')) {
        PaymentStore.loadPaymentMethods({ restaurantId: '' });
      }
    }
  };

  return (
    <Container selected={selected} p={1} key={id} onClick={() => onClick(id)} role="button" data-testid="saved-card">
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Flex alignItems="center" opacity={isRemovingCard ? 0.5 : 1}>
          {selected && <Icon name="tick" fill={selected ? 'neonTeal800' : 'black'} mr="0.5rem" />}
          <AnimationWrapper selected={selected}>
            <Flex mr={1}>
              <Icon name={brand || 'card'} />
            </Flex>
            <P variant={2}>ending in {lastFour}</P>
          </AnimationWrapper>
        </Flex>
        {!selected && !isRemovingCard && <RemoveButton onConfirm={removeCard} />}
      </Flex>

      {isRemovingCard && (
        <LoadingIndicatorWrapper alignItems="center" justifyContent="center">
          <StyledLoadingIndicator />
          <P ml="0.5rem" variant={3}>
            Removing...
          </P>
        </LoadingIndicatorWrapper>
      )}
    </Container>
  );
};

const StyledLoadingIndicator = styled(LoadingIndicator)`
  margin: 0;
`;

// Added drifting in tick mark animation
const AnimationWrapper = styled(Flex)`
  @keyframes driftIn {
    0% {
      transform: translateX(-5px);
    }
    50% {
      transform: translateX(-2.5px);
    }
    100% {
      transform: translateX(0);
    }
  }
  animation: ${({ selected }) => (selected ? 'driftIn 0.2s' : '')};
  align-items: center;
`;

const LoadingIndicatorWrapper = styled(Flex)`
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  position: absolute;
`;

const Container = styled(Flex)`
  background: ${({ selected, theme }) => (selected ? theme.colors.neonTeal100 : theme.colors.white)};
  border: 1px solid ${({ selected, theme }) => (selected ? theme.colors.neonTeal400 : theme.colors.grey200)};
  cursor: pointer;
  margin-top: 0.5rem;
  position: relative;

  :hover {
    border-color: ${({ theme }) => theme.colors.neonTeal400};
    background: ${({ selected, theme }) => (selected ? theme.colors.neonTeal100 : theme.colors.white)};
  }

  @media (min-width: ${breakpoints.md}) {
    border-width: 2px;
  }
`;

StoredCard.propTypes = {
  brand: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  lastFour: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool,
};

StoredCard.defaultProps = {
  selected: false,
};

const ObservedStoredCard = observer(StoredCard);
export { ObservedStoredCard as StoredCard };
